import { useEffect, useMemo } from "react";
import { Progress } from "@heroui/react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { useShallow } from "zustand/shallow";

import { appVersion } from "@/config/env";
import { useIsOnline } from "@/shared/hooks/useIsOnline";
import { appVersionIsBigger } from "@/shared/utils/common";
import { hooks } from "@/services/api/hooks";
import { PageDataPlaceholder } from "@/shared/components/ui/PageDataPlaceholder";
import { CloudArrowDownIcon, WifiSlashIcon, WrenchIcon } from "@phosphor-icons/react";
import { useBoundStore } from "@/app/store";
import { usePageLayout } from "@/app/hooks/usePageLayout";

interface AppStatusHandlerProps {
  children: React.ReactNode;
}

export function AppStatusHandler(props: AppStatusHandlerProps) {
  const { children } = props;
  const { data: appMetadata } = hooks.appMetadata.useAppMetadata();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();
  const isOnline = useIsOnline();
  const { userId, setUser } = useBoundStore(
    useShallow((state) => ({ userId: state.user.id, setUser: state.user.set }))
  );
  const { data: subscription } = hooks.subscription.useSubscriptionByUserId(userId);

  /**
   * Update user store with subscription data
   */
  useEffect(() => {
    (async () => {
      if (subscription) {
        setUser({ subscription });
      }
    })();
  }, [subscription, setUser]);

  // Force app updated
  useEffect(() => {
    if (needRefresh && appVersionIsBigger(appMetadata?.update.requiredVersion, appVersion)) {
      updateServiceWorker(true);
    }
  }, [appMetadata, needRefresh, updateServiceWorker]);

  // Determine which status page to show, if any
  if (appMetadata?.maintenance.active) {
    return (
      <StatusPageLayout title="Maintenance mode" description={appMetadata.maintenance.text} icon={<WrenchIcon />} />
    );
  }

  if (appVersionIsBigger(appMetadata?.update.requiredVersion, appVersion)) {
    return (
      <StatusPageLayout
        title="Update available"
        description={appMetadata?.update.webAppText || "A new version is available"}
        icon={<CloudArrowDownIcon />}
        actions={[<Progress aria-label="Loading..." isIndeterminate key="loading" />]}
      />
    );
  }

  if (!isOnline) {
    return (
      <StatusPageLayout
        title="Offline mode"
        description="Please check your internet connection and try again."
        icon={<WifiSlashIcon />}
      />
    );
  }



  return children;
}

interface StatusPageLayoutProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  actions?: React.ReactNode;
}

function StatusPageLayout({ title, description, icon, actions }: StatusPageLayoutProps) {
  const pageLayoutConfig = useMemo(() => ({ navbar: { title: "InkRows" } }), []);

  usePageLayout(pageLayoutConfig);

  return actions ? (
    <PageDataPlaceholder
      description={description}
      icon={icon as React.ReactElement}
      actions={actions as React.ReactElement}
      title={title}
    />
  ) : (
    <PageDataPlaceholder description={description} icon={icon as React.ReactElement} title={title} />
  );
}
